import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";

const inputCls = "h-12 w-full rounded-xl border border-neutral-200 px-4 text-[15px] outline-none focus:border-[var(--saffron)]";

export default function StaffLogin() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/staff/leads" replace />;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/login", form);
      setChallenge(data);
      setCode("");
    });
  };

  const verify = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        const { data } = await api.post("/auth/verify-otp", { challenge_id: challenge.challenge_id, code });
        setUser(data);
      } catch (err) {
        if (err.response?.status === 400 || err.response?.status === 429) setChallenge(null);
        throw err;
      }
    });
  };

  return (
    <div className="jaali-bg flex min-h-screen flex-col" data-testid="staff-login-page">
      <div className="tricolour-line" />
      <div className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm rounded-3xl border border-neutral-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]">
          <p className="font-display text-xl text-[#1f2328]">PropertiesforNRI <span className="text-sm text-[#138808]">Staff</span></p>
          {!challenge ? (
            <form onSubmit={login} className="mt-6 space-y-4" data-testid="staff-login-form">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="s-email">Email</label>
                <input id="s-email" type="email" autoComplete="username" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-login-email-input" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="s-pass">Password</label>
                <input id="s-pass" type="password" autoComplete="current-password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-login-password-input" />
              </div>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="staff-login-error">{error}</p>}
              <button disabled={busy || !form.email || !form.password} className="btn-saffron flex h-12 w-full items-center justify-center rounded-full font-semibold" data-testid="staff-login-submit-button">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-6 space-y-5" data-testid="staff-otp-form">
              <div className="flex items-start gap-3 rounded-xl bg-[var(--ivory)] p-3 text-sm text-neutral-600">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#138808]" />
                <span>We sent a 6-digit code to <b>{challenge.sent_to}</b>. It expires in 5 minutes.</span>
              </div>
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="staff-otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11" />)}
                </InputOTPGroup>
              </InputOTP>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="staff-otp-error">{error}</p>}
              <button disabled={busy || code.length !== 6} className="btn-saffron flex h-12 w-full items-center justify-center rounded-full font-semibold" data-testid="staff-otp-submit-button">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & sign in"}
              </button>
              <button type="button" onClick={() => setChallenge(null)} className="w-full text-sm text-neutral-500 hover:text-neutral-800" data-testid="staff-otp-back-button">
                Use a different account
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
